import { lazy, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { z } from 'zod';
import { useAuthStore } from '@/stores/auth';
import { PageFrame, Toolbar } from '@/components/executive/ExecAtoms';
import { LeadOwnerFilter } from './LeadOwnerFilter';
import { sourceDisplayLabel, LEAD_SOURCE_KEYS, LEAD_SOURCE_KEY_LABELS } from './sourceLabel';
import { LostReasonBadge } from '@/components/leads/LostReasonBadge';
import { DeskState, deskDate } from '../dashboard/roles/ProfileDeskParts';
import { useProfileResource } from '../dashboard/roles/useProfileResource';
const LeadsPage = lazy(() => import('./LeadsPage').then(module => ({ default: module.LeadsPage })));

const optionalText = z.string().nullish();
const leadSchema = z.object({
  id: z.string(), name: optionalText, phone: optionalText, email: optionalText,
  company: optionalText, location: optionalText, sourceName: optionalText,
  canonicalStage: optionalText, leadStage: optionalText, assignedToName: optionalText,
  notes: optionalText, requirement: optionalText, product: optionalText,
  followUpDate: optionalText, createdAt: z.string(), lostReason: optionalText,
});
const listSchema = z.object({
  rows: z.array(leadSchema), total: z.number().int().nonnegative(),
  ownerBreakdown: z.object({
    canFilterByOwner: z.boolean(), unassignedCount: z.number(),
    owners: z.array(z.object({ userId: z.string(), name: z.string(), count: z.number() })),
  }),
});
const parseList = (body: unknown) => listSchema.parse(body);
const stages = ['all', 'cold', 'followup', 'quote', 'neg', 'hot', 'won', 'lost'];

/** Constrained adapter for the existing Leads route and its shared list contract. */
export function CrmLeadsRoute() {
  const user = useAuthStore(s => s.user);
  return user?.role?.name === 'operations_manager'
    ? <CrmLeadRecords key={`${user.tenantId}:${user.id}`} /> : <LeadsPage />;
}

function CrmLeadRecords() {
  const location = useLocation();
  const [query, setQuery] = useState('');
  const [owner, setOwner] = useState('');
  const [stage, setStage] = useState('all');
  const [source, setSource] = useState('');
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<string | null>(location.state?.leadId ?? null);
  const params = new URLSearchParams({ skip: String(page * 50), take: '50' });
  if (query) params.set('q', query);
  if (owner) params.set('owner', owner);
  if (stage !== 'all') params.set('stage', stage);
  if (source) params.set('source', source);
  const resource = useProfileResource(`/leads-list?${params}`, parseList);
  const detail = resource.data?.rows.find(row => row.id === selected);
  const filter = (setter: (value: string) => void) => (value: string) => { setter(value); setPage(0); };
  return <PageFrame title="Leads" sub="Lead records and follow-up status" actions={<button onClick={resource.reload}>Refresh</button>}>
    <Toolbar left={<>
      <input aria-label="Search leads" placeholder="Search leads" value={query} onChange={e => filter(setQuery)(e.target.value)} className="rounded border p-2" />
      <select aria-label="Stage" value={stage} onChange={e => filter(setStage)(e.target.value)} className="rounded border p-2">
        {stages.map(value => <option key={value} value={value}>{value === 'all' ? 'All stages' : value}</option>)}
      </select>
      <select aria-label="Source" value={source} onChange={e => filter(setSource)(e.target.value)} className="rounded border p-2">
        <option value="">All sources</option>
        {LEAD_SOURCE_KEYS.map(value => <option key={value} value={value}>{LEAD_SOURCE_KEY_LABELS[value]}</option>)}
      </select>
    </>} />
    <DeskState resource={resource} />
    {resource.data && <>
      <LeadOwnerFilter breakdown={resource.data.ownerBreakdown} value={owner} onChange={filter(setOwner)} />
      <p className="my-3 text-sm">{resource.data.total} leads</p>
      <div className="overflow-x-auto rounded border bg-white">
        <table className="w-full text-left text-sm"><thead><tr>
          {['Lead', 'Phone', 'Source', 'Stage', 'Owner', 'Follow-up', 'Action'].map(label => <th className="p-3" key={label}>{label}</th>)}
        </tr></thead><tbody>{resource.data.rows.map(row => <tr key={row.id} className="border-t">
          <td className="p-3">{row.name || row.company || 'Name not recorded'}</td>
          <td className="p-3">{row.phone || 'Not recorded'}</td>
          <td className="p-3">{sourceDisplayLabel(row.sourceName)}</td>
          <td className="p-3">{row.canonicalStage || row.leadStage || 'Not recorded'}</td>
          <td className="p-3">{row.assignedToName || 'Unassigned'}</td>
          <td className="p-3">{deskDate(row.followUpDate ?? null)}</td>
          <td className="p-3"><button onClick={() => setSelected(row.id)}>View</button></td>
        </tr>)}</tbody></table>
        {resource.data.rows.length === 0 && <p className="p-4">No leads match these filters.</p>}
      </div>
      <div className="my-3 flex gap-4">
        <button disabled={page === 0} onClick={() => setPage(p => p - 1)}>Previous</button>
        <span>Page {page + 1}</span>
        <button disabled={(page + 1) * 50 >= resource.data.total} onClick={() => setPage(p => p + 1)}>Next</button>
      </div>
    </>}
    {detail && <section aria-label="Lead details" className="rounded border bg-white p-4">
      <h3 className="text-lg font-semibold">{detail.name || 'Lead details'}</h3>
      <dl className="my-3 grid gap-2 sm:grid-cols-2">
        {Object.entries({ Company: detail.company, Email: detail.email, Location: detail.location, Product: detail.product, Requirement: detail.requirement, Notes: detail.notes }).map(([label, value]) => <div key={label}><dt className="text-sm text-gray-500">{label}</dt><dd>{value || 'Not recorded'}</dd></div>)}
      </dl>
      <LostReasonBadge reason={detail.lostReason ?? null} />
      <div className="mt-4 flex gap-4"><Link to="/contacts" state={{ search: detail.phone || detail.name }}>Contact records</Link><button onClick={() => setSelected(null)}>Close details</button></div>
    </section>}
  </PageFrame>;
}
